import { Button, Card, Form, Input, Select, Table, TableColumnsType } from 'antd'
import { HistoryType, PagingParams, StakingNftType } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ButtonExport from 'component/button/button-export'
import LinkAddress from 'component/link/link-address'
import ButtonCopy from 'component/button/button-copy'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { HISTORY_TYPE, HISTORY_TYPE_ARRAY } from 'constants/history-type'
import useSearchParams from 'hooks/use-search-params'
import Overview from 'modules/stake-nft/listing/overview'
import { convertDateToString } from 'utils/format-time'
import { logInfo } from 'utils/logger'

import './style.scss'

interface TransactionSearchParams extends PagingParams {
  type?: number | null
  ownerAddress?: string
}

export default function PoolDetail() {
  const { ptoId, id: stakingNftId } = useParams()

  const [stakingNft, setStakingNft] = useState<StakingNftType | null>(null)
  const [dataTransactions, setDataTransactions] = useState([])
  const [total, setTotal] = useState<number>(0)

  const { searchParams, initPagination, onChangePagination, onSearch } =
    useSearchParams<TransactionSearchParams>()

  const getStakingNft = useCallback(async () => {
    try {
      if (stakingNftId) {
        const res = await AppAdmin.getDetailStakingTokenize(stakingNftId)
        if (res?.data.success === true) {
          setStakingNft(res?.data.data)
        }
      }
    } catch (error) {
      logInfo('Error: ', error)
    }
  }, [stakingNftId])

  const getDataTransactions = useCallback(async () => {
    try {
      if (ptoId && stakingNft) {
        const res = await AppAdmin.stakingTokenizeTransaction({
          page: searchParams.page,
          limit: searchParams.limit,
          type: searchParams.type,
          ownerAddress: searchParams.ownerAddress,
          contractAddress: stakingNft.contractAddress,
          tokenize: ptoId,
        })
        if (res?.data.success === true) {
          setDataTransactions(res?.data.data?.results)
          setTotal(res?.data.data?.totalResults)
        }
      }
    } catch (error) {
      logInfo('Error: ', error)
    }
  }, [
    ptoId,
    searchParams.limit,
    searchParams.ownerAddress,
    searchParams.page,
    searchParams.type,
    stakingNft,
  ])

  useEffect(() => {
    getStakingNft()
  }, [getStakingNft, stakingNftId])

  useEffect(() => {
    if (stakingNft) {
      getDataTransactions()
    }
  }, [getDataTransactions, stakingNft])

  const columns: TableColumnsType<HistoryType> = [
    {
      title: 'Address',
      dataIndex: 'ownerAddress',
      key: 'ownerAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <LinkAddress address={text} type="address" />
          {text ? <ButtonCopy text={text} /> : null}
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Type',
      dataIndex: 'byType',
      key: 'byType',
      render: (byType) => {
        const objectType = HISTORY_TYPE_ARRAY.find((c) => c.value === Number(byType))
        return <span>{objectType?.label || ''}</span>
      },
    },
    {
      title: 'Asset Token',
      dataIndex: 'assetToken',
      key: 'assetToken',
    },
    {
      title: 'Status',
      dataIndex: 'byStatus',
      key: 'byStatus',
      render: (status) =>
        status === 1 ? (
          <span className="status-pending">Pending</span>
        ) : status === 2 ? (
          <span className="status-success">Success</span>
        ) : (
          <span className="status-fail">Fail</span>
        ),
    },
    {
      title: 'txHash',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <LinkAddress address={text} type="tx" />
        </div>
      ),
    },
    {
      title: 'CreatedAt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => convertDateToString(text),
    },
  ]

  const statusOptions = [
    {
      value: null,
      label: 'All',
    },
    {
      value: HISTORY_TYPE.STAKE_NFT,
      label: 'Stake',
    },
    {
      value: HISTORY_TYPE.UN_STAKE_NFT,
      label: 'UnStake',
    },
    {
      value: HISTORY_TYPE.STAKE_NFT_CLAIM_REWARD,
      label: 'Claimable rewards',
    },
  ]

  const additionalItems = [BREADCRUMBS.POOL_DETAIL]

  return (
    <div className="main-section pool-detail">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="dashboard-container">
        <Overview />
      </div>
      <div className="main-title">{stakingNft?.name}</div>
      <Card className="formSearch">
        <Form layout="inline" style={{ padding: 20, justifyContent: 'start' }}>
          <Form.Item>
            <Input
              allowClear
              type="text"
              placeholder="By address"
              value={searchParams.ownerAddress || ''}
              onChange={(e) => {
                onSearch({
                  ownerAddress: e.target.value,
                } as TransactionSearchParams)
              }}
            />
          </Form.Item>
          <Form.Item>
            <Select
              allowClear
              placeholder="By type"
              options={statusOptions}
              value={searchParams.type}
              onChange={(value) => {
                onSearch({ type: value } as TransactionSearchParams)
              }}
              style={{ width: '200px', textAlign: 'start' }}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={() => onSearch()}>
              Search
            </Button>
          </Form.Item>
          <Form.Item>
            <ButtonExport
              fileType="staking_nft_transactions"
              disabled={!stakingNft?.contractAddress}
              onExport={() =>
                AppAdmin.exportStakingTokenizeTransaction({
                  contractAddress: stakingNft?.contractAddress as string,
                  ownerAddress: searchParams.ownerAddress,
                  type: searchParams.type,
                  tokenize: ptoId,
                })
              }
            />
          </Form.Item>
        </Form>
      </Card>

      <div className="listUsers">
        <div className="main-title">Transaction</div>
        <Card style={{ margin: '0 auto' }}>
          <Table<HistoryType>
            columns={columns}
            dataSource={dataTransactions}
            scroll={{ x: 1000 }}
            bordered
            pagination={initPagination(total)}
            onChange={onChangePagination}
          />
        </Card>
      </div>
    </div>
  )
}
